// AS-10834: stock at the PO's delivery location, shown next to the all-locations
// "Actual Inventory Quantity". Same OLAP measure as the total column, narrowed by a
// location_id filter, so the two columns come from the same snapshot.
//
// The HTTP getter is injected (defaulting to the real http.get) so this module
// unit-tests without chain-loading the models.

import { get as httpGet } from '@/services/http';

export const LOCATION_STOCK_ENDPOINT = '/master-data/variants';
export const LOCATION_STOCK_API_CODE = '[Actual Inventory Quantity]';
const BATCH_SIZE = 250;

export type LocationStockByVariant = Record<string, number>;

interface Deps {
  get: (url: string, params?: any) => Promise<any>;
}

export function locationStockHeaderText(locationName?: string | null): string {
  const name = (locationName || '').trim();
  return name ? `Stock at ${name}` : 'Stock at Location';
}

export function buildLocationStockParams(variantIds: string[], locationId: number | string) {
  const columnFilter = [
    { code: 'variant_id', operator: 'in', values: variantIds, dataType: 'text' },
    { code: 'location_id', operator: 'eq', values: [String(locationId)], dataType: 'number' },
  ];
  return {
    column_filter: JSON.stringify(columnFilter),
    columns: ['variant_id', LOCATION_STOCK_API_CODE].join(','),
    limit: variantIds.length,
  };
}

function toNumber(value: any): number {
  if (value === null || value === undefined || value === '') { return 0; }
  const n = typeof value === 'number' ? value : Number(String(value).replace(/,/g, ''));
  return Number.isFinite(n) ? n : 0;
}

/**
 * Map a /master-data/variants response (OLAP rows of `items`, positionally matched
 * to `columns[].code`) to variant_id -> quantity. Requested variants with no row
 * (never stocked at the location) default to 0.
 */
export function parseLocationStockResponse(response: any, requestedIds: string[] = []): LocationStockByVariant {
  const result: LocationStockByVariant = {};
  requestedIds.forEach((id) => { result[id] = 0; });

  const columns: any[] = response?.data?.columns || [];
  const rows: any[] = response?.data?.rows || [];
  const variantIdx = columns.findIndex((c) => c?.code === 'variant_id');
  const qtyIdx = columns.findIndex((c) => c?.code === LOCATION_STOCK_API_CODE);
  if (variantIdx < 0 || qtyIdx < 0) { return result; }

  rows.forEach((row) => {
    const items = row?.items || [];
    const variantItem = items[variantIdx];
    const qtyItem = items[qtyIdx];
    const variantId = String(variantItem?.original_value ?? variantItem?.value ?? '').replace(/,/g, '');
    if (!variantId) { return; }
    result[variantId] = toNumber(qtyItem?.original_value ?? qtyItem?.value);
  });
  return result;
}

/**
 * Fetch per-location stock for the given variants. No location or no variants ->
 * {} without a request. Batched so a large PO stays under the filter size limit.
 */
export async function fetchLocationStock(
  variantIds: Array<number | string>,
  locationId: number | string | null | undefined,
  deps: Deps = { get: httpGet },
): Promise<LocationStockByVariant> {
  if (locationId === null || locationId === undefined || locationId === '') { return {}; }
  const unique = Array.from(new Set(
    (variantIds || []).filter((id) => id !== null && id !== undefined && id !== '' && String(id) !== 'null').map(String),
  ));
  if (unique.length === 0) { return {}; }

  const chunks: string[][] = [];
  for (let i = 0; i < unique.length; i += BATCH_SIZE) {
    chunks.push(unique.slice(i, i + BATCH_SIZE));
  }
  const parts = await Promise.all(chunks.map((chunk) =>
    deps.get(LOCATION_STOCK_ENDPOINT, buildLocationStockParams(chunk, locationId))
      .then((response) => parseLocationStockResponse(response, chunk)),
  ));
  return Object.assign({}, ...parts);
}
